import { DesignerKit } from "@/components/DesignerKit";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { MascotDoodle } from "@/components/brand/MascotDoodle";
import { Brush } from "@/components/brand/Lettering";
import { errText, submitSotm } from "@/lib/api";

const TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX = 10 * 1024 * 1024;
const input = "h-12 w-full border-b-[1.5px] border-ink/40 bg-transparent text-[15px] focus:border-ink focus:outline-none";

const Field = ({ label, children, hint }) => (
  <label className="block"><span className="label text-ink/55">{label}</span>{children}{hint && <span className="mt-1 block text-[12px] text-ink/45">{hint}</span>}</label>
);

const Check = ({ checked, onChange, children, testid }) => (
  <label className="flex items-start gap-3 text-[14px]"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-ink" data-testid={testid} /><span>{children}</span></label>
);

const FilePick = ({ label, file, setFile, testid, required }) => (
  <Field label={`${label}${required ? " *" : " (optional)"}`} hint="PNG, JPG or WebP. Max 10MB.">
    <div className="mt-3 flex items-center gap-4">
      <span className="tile flex h-24 w-24 items-center justify-center overflow-hidden">{file ? <img src={URL.createObjectURL(file)} alt="" className="h-full w-full object-cover" /> : <span className="label text-ink/30">None</span>}</span>
      <input type="file" accept=".png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files[0] || null)} className="text-sm file:mr-3 file:border-[1.5px] file:border-ink file:bg-transparent file:px-3 file:py-2 file:font-mono file:text-xs file:uppercase" data-testid={testid} />
    </div>
  </Field>
);

export default function SotmSubmit() {
  const [f, setF] = useState({ full_name: "", age: "", city: "", instagram: "", email: "", design_title: "", story: "", parent_email: "" });
  const [design, setDesign] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [checks, setChecks] = useState({ original: false, consent: false, parent: false });
  const [state, setState] = useState({ status: "idle", error: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const age = Number(f.age);
  const minor = f.age !== "" && age < 16;

  const validate = () => {
    if (!f.full_name.trim() || !f.city.trim() || !f.email.trim() || !f.design_title.trim() || f.story.trim().length < 10) return "Please fill in every required field (story needs at least 10 characters).";
    if (!Number.isInteger(age) || age < 13 || age > 19) return "Sus of the Month is open to ages 13 to 19 only.";
    if (!design) return "Please upload your design.";
    for (const file of [design, photo].filter(Boolean)) {
      if (!TYPES.includes(file.type)) return "Images must be PNG, JPG or WebP.";
      if (file.size > MAX) return "Images must be 10MB or smaller.";
    }
    if (!checks.original || !checks.consent) return "Please confirm both checkboxes.";
    if (minor && (!f.parent_email.trim() || !checks.parent)) return "Under 16? Add a parent or guardian email and confirm their approval.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return setState({ status: "idle", error: err });
    setState({ status: "loading", error: "" });
    const form = new FormData();
    Object.entries(f).forEach(([k, v]) => v && (k !== "parent_email" || minor) && form.append(k, v));
    form.append("original_work", checks.original);
    form.append("public_consent", checks.consent);
    form.append("parent_approved", minor && checks.parent);
    form.append("design", design);
    if (photo) form.append("photo", photo);
    try {
      await submitSotm(form);
      setState({ status: "done", error: "" });
      window.scrollTo(0, 0);
    } catch (ex) {
      setState({ status: "idle", error: errText(ex) });
    }
  };

  const seo = <Seo title="Submit your design: Sus of the Month" description="GTA teens aged 13 to 19: submit your original tee design to Sus of the Month. The community votes, we print the winner." path="/sus-of-the-month/submit" />;
  if (state.status === "done")
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-start px-5 py-24 sm:px-10" data-testid="sotm-submit-success">
        {seo}
        <MascotDoodle variant="peace" size={240} />
        <Brush lines={["You're in", "the lineup 👀"]} className="mt-10 text-[clamp(2.75rem,6vw,4.5rem)]" />
        <p className="mt-10 max-w-md text-[15px] leading-relaxed text-ink/70">Thanks for sending your design. Our crew reviews every submission. Once it's approved, it shows up in this month's lineup for voting.</p>
        <Link to="/sus-of-the-month" className="btn btn-ink mt-8" data-testid="sotm-success-gallery-link">See the lineup <ArrowRight size={16} /></Link>
      </div>
    );

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 sm:px-10" data-testid="sotm-submit-page">
      {seo}
      <header className="grid gap-8 pb-12 pt-16 lg:grid-cols-12 lg:pt-24">
        <h1 className="display text-[clamp(3.5rem,8vw,7rem)] lg:col-span-7">Get in the lineup</h1>
        <p className="max-w-md text-[15px] leading-relaxed text-ink/70 lg:col-span-5 lg:self-end">Open to teens aged 13 to 19. One original design per month. If you win, your design becomes a tee in our shop, with your name and story on it.</p>
      </header>
      <DesignerKit />
      <form onSubmit={submit} noValidate className="grid gap-x-16 gap-y-10 border-t border-ink/10 pt-12 lg:grid-cols-2" data-testid="sotm-submit-form">
        <div className="space-y-8">
          <Field label="Full name *"><input value={f.full_name} onChange={set("full_name")} className={input} data-testid="sotm-input-name" /></Field>
          <div className="grid grid-cols-2 gap-6">
            <Field label="Age *"><input type="number" min="13" max="19" value={f.age} onChange={set("age")} className={input} data-testid="sotm-input-age" /></Field>
            <Field label="City *"><input value={f.city} onChange={set("city")} className={input} data-testid="sotm-input-city" /></Field>
          </div>
          <Field label="Email *"><input type="email" value={f.email} onChange={set("email")} className={input} data-testid="sotm-input-email" /></Field>
          <Field label="Instagram (optional)"><input value={f.instagram} onChange={set("instagram")} placeholder="@yourhandle" className={input} data-testid="sotm-input-instagram" /></Field>
          {minor && (
            <div className="space-y-5 border-l-2 border-lime pl-5" data-testid="sotm-parent-block">
              <Field label="Parent / guardian email *" hint="Under 16? We need a parent or guardian to approve."><input type="email" value={f.parent_email} onChange={set("parent_email")} className={input} data-testid="sotm-input-parent-email" /></Field>
              <Check checked={checks.parent} onChange={(v) => setChecks({ ...checks, parent: v })} testid="sotm-check-parent">My parent/guardian has approved this submission</Check>
            </div>
          )}
        </div>
        <div className="space-y-8">
          <Field label="Design title *"><input value={f.design_title} onChange={set("design_title")} maxLength={60} className={input} data-testid="sotm-input-title" /></Field>
          <FilePick label="Your design" required file={design} setFile={setDesign} testid="sotm-input-design" />
          <FilePick label="A photo of you" file={photo} setFile={setPhoto} testid="sotm-input-photo" />
          <Field label={`The story behind your design * (${f.story.length}/600)`}>
            <textarea value={f.story} onChange={set("story")} maxLength={600} rows={5} className="mt-2 w-full border-[1.5px] border-ink/40 bg-transparent p-3 text-[15px] focus:border-ink focus:outline-none" data-testid="sotm-input-story" />
          </Field>
          <div className="space-y-3">
            <Check checked={checks.original} onChange={(v) => setChecks({ ...checks, original: v })} testid="sotm-check-original">This is my original work</Check>
            <Check checked={checks.consent} onChange={(v) => setChecks({ ...checks, consent: v })} testid="sotm-check-consent">I agree my name, photo, design and story can be shown publicly and printed on tees</Check>
          </div>
          {state.error && <p className="font-mono text-xs text-alert" role="alert" data-testid="sotm-submit-error">{state.error}</p>}
          <button disabled={state.status === "loading"} className="btn btn-lime w-full" data-testid="sotm-submit-button">{state.status === "loading" ? "Uploading…" : "Submit my design"} <ArrowRight size={16} /></button>
        </div>
      </form>
    </div>
  );
}
